import { get, first } from 'lodash';
import { initialState, pageOrder } from './initialState';

const MULTI_LAYOUT = 'scissors';

export const mapTourUIFromResponseToStore = (data, isBranded) => ({
  title: !isBranded && data.fullAddress ? data.fullAddress : initialState.ui.title,
  spa: getPartOfNameFromString(get(data, 'settings.layout'), 0) !== MULTI_LAYOUT,
  layout: getPartOfNameFromString(get(data, 'settings.layout'), 0) || initialState.ui.layout,
  mode: getPartOfNameFromString(get(data, 'settings.layout'), 1) || initialState.ui.mode
});

export const mapTourDataFromResponseToStore = (data, isBranded) => ({
  id: data.id,
  settings: data.settings ? mapSettings(data.settings) : initialState.data.settings,
  pages: data.settings ? mapPages(data.settings, isBranded) : initialState.data.pages,
  fullAddress: data.fullAddress || initialState.data.fullAddress,
  address: data.address ? mapAddress(data.address) : initialState.data.address,
  details: mapDetails({...data.details, ...{address: data.fullAddress}}),
  video: data.video ? mapVideo(data.video) : [],
  tour360: data.tour360 ? map360Tour(data.tour360) : [],
  videoSlideshow: data.videoSlideshow || initialState.data.videoSlideshow,
  photos: mapPhotos(data.photos || []),
  floorplanPhotos: mapPhotos(get(data, 'floorplan.photos') || []),
  floorplanFiles: get(data, 'floorplan.files') || [],
  documents: data.documents || initialState.data.documents, // [{name, url}]
  map: data.map || initialState.data.map,
  walkScore: data.walkScore ? mapWalkScore(data.walkScore) : initialState.data.walkScore,
  agent: data.agent ? mapAgent(data.agent) : initialState.data.agent,
  multiAgents: data.multiAgents ? data.multiAgents.map(mapAgent) : initialState.data.multiAgents,
  footer: data.footer ? mapFooter(data.footer) : initialState.data.footer,
  payments: data.payments ? {...initialState.data.payments, ...data.payments} : initialState.data.payments,
  branding: data.branding || initialState.data.branding
});

const mapWalkScore = data => ({
  address: data.address,
  enableWalkScore: 'enableWalkScore' in data ? data.enableWalkScore : initialState.data.walkScore.enableWalkScore,
  enableHoodq: data.enableHoodq,
  hoodqUrl: data.hoodqUrl
});

const mapSettings = data => ({
  enabled: !!data.enabled,
  sold: !!data.sold,
  videoSlideshow: !!data.videoSlideshow,
  video: !!data.video,
  slideshow: !!data.slideshow,
  tour360: !!data.top360,
  gallery: !!data.gallery,
  details: !!data.details,
  floorplan: !!data.floorplan,
  documents: !!data.documents,
  map: !!data.map,
  walkScore: !!data.walkScore,
  topSection: mapTopSection(get(data, 'topSection.value')) || initialState.data.settings.topSection,
  topSectionPhotos: mapPhotos(get(data, 'topSectionPhotos') || []) || initialState.data.settings.topSectionPhotos,
  numberOfPhotosOnIntro: parseInt(data.numberOfPhotosOnIntro) || initialState.data.settings.numberOfPhotosOnIntro,
  slideshowConfig: data.slideshowConfig || initialState.data.settings.slideshowConfig,
  layoutConfig: data.layoutConfig || initialState.data.settings.layoutConfig,
  music: get(data, 'music.url') || initialState.data.settings.music,
  exitPopup: !!data.exitPopup,
  leadCapture: data.leadCapture || initialState.data.settings.leadCapture,
  openHouse: data.openHouse ? mapOpenHouse(data.openHouse) : initialState.data.settings.openHouse,
  access: data.access || initialState.data.settings.access
});

// video, slideshow, tour360 -> 360-tour, none
const mapTopSection = value => value === 'tour360' ? '360-tour' : value;

const mapPages = (settings, isBranded) => ([
  { componentName: 'Details', value: 'details', label: 'Details', visible: !!settings.details, order: getPageOrder(settings.layout).details },
  { componentName: 'Video', value: 'video', label: 'Video', visible: !!settings.video, order: getPageOrder(settings.layout).video },
  { componentName: 'Gallery', value: 'gallery', label: 'Gallery', visible: !!settings.gallery, order: getPageOrder(settings.layout).gallery },
  { componentName: 'Tour360', value: '360-tour', label: '360 tour', visible: !!settings.tour360, order: getPageOrder(settings.layout)['360-tour'] },
  { componentName: 'VideoSlideshow', value: 'video-slideshow', label: 'Video Slideshow', visible: !!settings.videoSlideshow, order: getPageOrder(settings.layout)['video-slideshow'] },
  { componentName: 'Slideshow', value: 'slideshow', label: 'Slideshow', visible: !!settings.slideshow, order: getPageOrder(settings.layout).slideshow },
  { componentName: 'Floorplan', value: 'floorplan', label: 'Floorplan', visible: !!settings.floorplan, order: getPageOrder(settings.layout).floorplan },
  { componentName: 'Documents', value: 'documents', label: 'Documents', visible: !!settings.documents, order: getPageOrder(settings.layout).documents },
  { componentName: 'WalkScore', value: 'walkScore', label: 'Walk Score', visible: !!settings.walkScore, order: getPageOrder(settings.layout).walkScore },
  { componentName: 'Map', value: 'map', label: 'Map', visible: !!settings.map, order: getPageOrder(settings.layout).map },
  { componentName: 'Contact', value: 'contact', label: 'Contact', visible: isBranded, order: getPageOrder(settings.layout).contact }
]);

const mapAddress = data => ({
  address: data.address,
  city: data.city,
  country: get(data, 'country.label') || initialState.data.address.country,
  shortStateName: get(data, 'state.value') || data.state,
  longStateName: get(data, 'state.label') || data.state,
  zipcode: data.zipcode
});

const mapDetails = details => ({
  address: { label: 'Address', value: details.address },
  description: { label: 'Description', value: details.description },
  propertyType: { label: 'Property type', value: details.propertyType },
  buildingType: { label: 'Building type', value: details.buildingType },
  heatingType: { label: 'Heating type', value: details.heatingType },
  price: { label: 'Price', value: details.price && `$${details.price}` },
  parking: { label: 'Parking', value: details.parking },
  bedrooms: { label: 'Bedrooms', value: details.bedrooms },
  bathrooms: { label: 'Bathrooms', value: details.bathrooms },
  basement: { label: 'Basement', value: yesOrNo(details.basement) },
  stories: { label: 'Stories', value: details.stories },
  appliances: { label: 'Appliances', value: details.appliances },
  garage: { label: 'Garage', value: yesOrNo(details.garage) },
  lot: { label: 'Lot Size', value: details.lot },
  totalSquareFootage: { label: 'Total Square Footage', value: details.totalSquareFootage && `${details.totalSquareFootage} sqft` },
  yearBuilt: { label: 'Year Built', value: details.yearBuilt },
  taxes: { label: 'Taxes', value: details.taxes },
  maintenanceFee: { label: 'Maintenance fee', value: details.maintenanceFee && `$${details.maintenanceFee}` },
  guestHome: { label: 'Guest Home', value: yesOrNo(details.guestHome) },
  bedroomsGH: { label: 'Bedrooms', value: details.guestHome && details.bedroomsGH },
  bathroomsGH: { label: 'Bathrooms', value: details.guestHome && details.bathroomsGH },
  totalSquareFootageGH: { label: 'Total Square Footage', value: details.guestHome && details.totalSquareFootageGH && `${details.totalSquareFootageGH} sqft` },
  shop: { label: 'Shop', value: yesOrNo(details.shop) },
  totalSquareFootageShop: { label: 'Total Square Footage', value: details.shop && details.totalSquareFootageShop && `${details.totalSquareFootageShop} sqft` }
});

const mapVideo = data => {
  if (data.videos) {
    return data.videos;
  } else if (data.isYoutube || data.isVimeo || data.isIframe) {
    const name = (data.isYoutube && 'youtube') || (data.isVimeo && 'vimeo') || (data.isIframe && 'iframe');

    return [{
      name,
      youtube: {
        branded: name === 'youtube' ? data.youtubeUrlBranded : '',
        unbranded: name === 'youtube' ? data.youtubeUrlUnbranded : ''
      },
      vimeo: {
        branded: name === 'vimeo' ? data.vimeoUrlBranded : '',
        unbranded: name === 'vimeo' ? data.vimeoUrlUnbranded : ''
      },
      iframe: {
        branded: name === 'iframe' ? data.iframeUrlBranded : '',
        unbranded: name === 'iframe' ? data.iframeUrlUnbranded : ''
      }
    }];
  }
};

const map360Tour = data => {
  if (data.tours) {
    return data.tours;
  } else if (data.matterport || data.iguide || data.cloudPano) {
    const name = (data.matterport && 'matterport') || (data.iguide && 'iguide') || (data.cloudPano && 'cloudPano');

    return [{
      name,
      matterport: name === 'matterport' ? data.matterportUrl : '',
      iguide: name === 'iguide' ? data.iguideUrl : '',
      cloudPano: name === 'cloudPano' ? data.cloudPanoUrl : ''
    }];
  }
};

const mapAgent = data => ({
  id: data.id,
  userId: data.userId,
  agentName: data.agentName,
  agentTitle: data.agentTitle,
  brokerageName: data.brokerageName,
  avatar: first(mapPhotos(data.avatar || [])),
  logo: first(mapPhotos(data.logo || [])),
  address: data.address,
  city: data.city,
  country: get(data, 'country.label') || initialState.data.agent.country,
  state: data.state,
  zipcode: data.zipcode,
  email: data.email,
  phone: data.phone
});

const mapFooter = data => ({
  companyName: data.companyName,
  companyURL: data.companyURL,
  copyrightInfo: data.copyrightInfo,
  privacyPolicy: data.privacyPolicy,
  termsAndConditions: data.termsAndConditions,
  poweredBy: data.poweredBy === false ? false : true
});

const mapPhotos = photos =>
  photos.map(item => ({
    original: get(item.urls.find(el => el.size === 'orig'), 'url'),
    xs: get(item.urls.find(el => el.size === '500x380'), 'url'),
    md: get(item.urls.find(el => el.size === '1024x1024'), 'url'),
    xl: get(item.urls.find(el => el.size === '2560x1440'), 'url')
      || get(item.urls.find(el => el.size === '1920x1080'), 'url')
      || get(item.urls.find(el => el.size === '1600x900'), 'url')
  }));

const mapOpenHouse = data =>  {
  data['days'] = data['days'].filter(item => {
    const itemDay = new Date(item.day);
    const now = new Date();

    return now.getMonth() <= itemDay.getMonth() && now.getDate() <= itemDay.getDate();
  });
  return data;
};

const getPartOfNameFromString = (name, index, separator = '-') => name && name.split(separator)[index];

const yesOrNo = value => {
  if (value === undefined) {
    return undefined;
  }

  return value ? 'Yes' : 'No';
};

const getPageOrder = (layout) => {
  const layoutName = getPartOfNameFromString(layout, 0);
  return layoutName in pageOrder ? pageOrder[layoutName] : pageOrder.default;
};
